import React from "react";
import { Alert, Box, Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { formatMoney } from "../../../utils/erp";
import { createOnboardingHelpSession, reportOnboardingHelpEvent } from "../../../utils/onboardingHelpTelemetry";

const PLANS = [1, 2, 3, 6, 9, 12, 18, 24];
const money = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Replica la fórmula de resolvePaymentSettlements (backend): simulación informativa,
// NO altera la caja ni promete acreditación bancaria automática.
export function estimateManualProcessing(processing = {}, gross = 0, installments = 1) {
  const amount = Math.max(0, money(gross));
  const rate = Number(processing?.commissionPercent || 0);
  const fixed = Number(processing?.commissionFixed || 0);
  const taxRate = Number(processing?.commissionTaxPercent || 0);
  const configured = (processing?.installmentFees || []).find((row) => Number(row.installments) === Number(installments));
  const feeRate = Number(configured?.percent || 0);
  const commission = money(amount * (rate / 100) + fixed);
  const financing = money(amount * (feeRate / 100));
  const tax = money(commission * (taxRate / 100));
  return { amount, commission, financing, tax, net: money(Math.max(0, amount - commission - financing - tax)), planConfigured: Boolean(configured) };
}

export default function CashProcessingExplainer({ project, processing, currency = "ARS" }) {
  const helpSession = React.useRef(createOnboardingHelpSession());
  const [showPreview, setShowPreview] = React.useState(false);
  const [amount, setAmount] = React.useState("100000");
  const [installments, setInstallments] = React.useState("3");
  const preview = estimateManualProcessing(processing, amount, installments);
  return (
    <Stack spacing={1.15} sx={{ mt: 1.2 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <strong>Cómo se calcula:</strong> comisión sobre el bruto + cargo fijo; impuesto sobre esa comisión;
        costo adicional del plan de cuotas sobre el bruto. En una venta el cliente paga el total bruto, mientras
        el procesador puede acreditar un neto menor. Estas tasas son estimadas hasta contrastarlas con tu contrato.
      </Alert>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        <strong>Por cuotas:</strong> podés definir costos para cada cantidad ofrecida en ventas (1, 2, 3, 6, 9, 12, 18 y 24).
        Una tasa de 0% equivale a costo adicional cero; confirmá que no sea un dato faltante.
        <strong> Por monto:</strong> todavía no hay escalas automáticas (por ejemplo, 2% hasta $100.000 y 1,5% por encima).
        Tampoco hay desglose automático de retenciones por jurisdicción, IVA y otros conceptos: se ajustan en el movimiento.
      </Typography>
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        El campo «impuesto s/comisión» no es el IVA del producto. Se aplica sólo a la comisión del procesador;
        la acreditación en días es orientativa y no crea una conciliación bancaria ni un pago programado.
      </Alert>
      <Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setShowPreview((previous) => !previous);
            if (!showPreview) reportOnboardingHelpEvent(project, "cash_fee_preview_opened", {}, helpSession.current);
          }}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          {showPreview ? "Ocultar simulación" : "Simular cuánto recibiría"}
        </Button>
      </Box>
      {showPreview ? (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)" }}>
          <Typography sx={{ fontWeight: 950, mb: 1 }}>Ejemplo estimado: no registra una operación</Typography>
          <Grid container spacing={1.1}>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="number" size="small" label="Importe de venta" value={amount} onChange={(event) => setAmount(event.target.value)} inputProps={{ min: 0, step: .01 }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Cuotas" value={installments} onChange={(event) => setInstallments(event.target.value)}>
                {PLANS.map((plan) => <MenuItem key={plan} value={String(plan)}>{plan}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          {!preview.planConfigured ? <Alert severity="warning" sx={{ mt: 1 }}>Este plan no tiene una tasa configurada y se estimará al 0%. Revisá el contrato antes de cobrar.</Alert> : null}
          <Stack spacing={.45} sx={{ mt: 1.2 }}>
            <Typography variant="body2">Bruto: <strong>{formatMoney(preview.amount, currency)}</strong></Typography>
            <Typography variant="body2">Comisión: −{formatMoney(preview.commission, currency)}</Typography>
            <Typography variant="body2">Impuesto a la comisión: −{formatMoney(preview.tax, currency)}</Typography>
            <Typography variant="body2">Costo de cuotas: −{formatMoney(preview.financing, currency)}</Typography>
            <Typography sx={{ fontWeight: 950 }}>Neto antes de retenciones: {formatMoney(preview.net, currency)}</Typography>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}
